// API client: all calls to /api/* endpoints.
// Automatically appends ?code= from sessionStorage.

function getCode(): string {
  return sessionStorage.getItem('esim_radar_code') ?? '';
}

function apiUrl(path: string, params: Record<string, string> = {}): string {
  const url = new URL(path, window.location.href);
  url.searchParams.set('code', getCode());
  for (const [k, v] of Object.entries(params)) {
    if (v) url.searchParams.set(k, v);
  }
  return url.toString();
}

async function apiFetch<T>(url: string): Promise<T> {
  const resp = await fetch(url);
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: 'Unknown error' })) as { error?: string };
    throw new Error(err.error ?? `API error ${resp.status}`);
  }
  const data = await resp.json() as { ok: boolean; data?: T; error?: string };
  if (!data.ok) throw new Error(data.error ?? 'API returned ok:false');
  return data.data as T;
}

export interface NewsItem {
  id: number;
  week_number: number;
  provider_id: string;
  headline: string | null;
  category: string | null;
  summary: string | null;
  source_name: string | null;
  source_url: string | null;
  date: string | null;
  significance: string | null;
}

export interface SnapshotData {
  week_number: number;
  iso_date: string;
  total_mentions: number | null;
  themes: Theme[];
  actions: Action[];
  checklist: ChecklistItem[];
  pipeline_stats: PipelineStats | null;
  data_sources: DataSource[];
  sentiment_distribution: SentimentDistribution | null;
  signals: ReviewSignal[];
  providers: Provider[];
  news?: NewsItem[];
  // True when at least one earlier week exists in the DB. When false,
  // any week-over-week framing (trend arrows, biggest mover) would be invented,
  // so the UI must hide those affordances.
  has_prior_week?: boolean;
}

export interface Provider {
  id: string;
  name: string;
  category: 'consumer_esim' | 'corporate_iot' | 'airline_wifi';
  description: string | null;
  homepage_url: string | null;
  pricing_url: string | null;
  // 1 = URL responded OK last time we checked; 0 = 404 / timeout / gone.
  // Undefined for legacy rows never checked — treat as unknown, still clickable.
  homepage_reachable?: number;
  pricing_reachable?: number;
  urls_checked_at?: string | null;
  active: number;
  current_week_signals?: ReviewSignal[];
}

export interface ReviewSignal {
  id: number;
  provider_id: string;
  source: string;
  mention_count: number;
  sentiment_positive: number | null;
  sentiment_neutral: number | null;
  sentiment_negative: number | null;
  sample_quotes: string | null;
  snapshot_week: number;
}

export interface Theme {
  title: string;
  status: 'CRITICAL' | 'WATCH' | 'STABLE';
  trend: string;
  mentionCount: number;
  finding: string;
  impact: string;
  relatedPlayers: Array<{ id: string; name: string; unverified?: boolean }>;
  sourceExamples: string[];
}

export interface Action {
  id: string;
  focus: 'PRODUCT' | 'SUPPORT' | 'GROWTH' | 'MARKET_WATCH';
  priority: 'P1' | 'P2' | 'P3';
  title: string;
  description: string;
  relatedTheme?: string;
}

export interface ChecklistItem {
  label: string;
  done: boolean;
  notes?: string;
}

export interface PipelineStats {
  providers_fetched: number;
  providers_failed: number;
  plans_inserted: number;
  plans_verified: number;
  signals_inserted: number;
  duration_ms: number;
}

export interface DataSource {
  name: string;
  type: string;
  records_fetched: number;
  success: boolean;
}

export interface SentimentDistribution {
  positive: number;
  neutral: number;
  negative: number;
  total: number;
}

export interface Plan {
  id: number;
  provider_id: string;
  plan_name: string | null;
  region: string | null;
  region_type: string | null;
  data_gb: number | null;
  data_unlimited: number;
  validity_days: number | null;
  price_usd: number | null;
  price_local: string | null;
  promo_label: string | null;
  promo_price_usd: number | null;
  source_url: string | null;
  fetched_at: string;
  is_verified: number;
  snapshot_week: number;
  // 1 = URL responded OK last time we checked; 0 = 404/timeout/blocked.
  // Undefined for legacy rows never checked — treat as unknown, not broken.
  source_reachable?: number;
  source_checked_at?: string | null;
}

export interface Diff {
  id: number;
  week_number: number;
  entity_type: string;
  entity_id: string;
  field: string;
  prev_value: string | null;
  new_value: string | null;
  delta: number | null;
  delta_pct: number | null;
  is_biggest_mover: number;
  provider?: Provider;
}

export interface PriceComparison {
  id: number;
  week_number: number;
  provider_id: string;
  region: string;
  best_price_per_gb_usd: number | null;
  best_plan_id: number | null;
  best_promo_adjusted_price_usd: number | null;
  validity_adjusted_price_usd: number | null;
  provider?: Provider;
}

export interface PricingData {
  week: number;
  region: string;
  comparisons: PriceComparison[];
  plans: Plan[];
  providers: Provider[];
}

export interface DiffsData {
  week: number;
  diffs: Diff[];
  biggest_mover: Diff | null;
}

export interface ProviderDetail {
  provider: Provider;
  week: number;
  plans: Plan[];
  signals: ReviewSignal[];
  diffs: Diff[];
}

export interface BenchmarkRow {
  provider_id: string;
  provider_name: string;
  region: string | null;
  region_type: string | null;
  plan_name: string | null;
  data_gb: number | null;
  data_unlimited: number;
  validity_days: number | null;
  rival_price_usd: number;
  mode: 'per_gb' | 'per_day';
  rival_unit: number | null;
  esimio_price_usd: number | null;
  esimio_unit: number | null;
  esimio_data_gb: number | null;
  esimio_days: number | null;
  delta_pct: number | null;
  status: 'ok' | 'no_baseline' | 'not_comparable';
  is_verified: number;
  source_url: string | null;
}

export interface BenchmarkData {
  requested_week: number;
  used_week: number;
  fell_back: boolean;
  baseline_provider: string;
  region: string;
  rows: BenchmarkRow[];
}

export interface PriceChangeRow {
  provider_id: string;
  provider_name: string;
  region: string | null;
  region_type: string | null;
  plan_name: string | null;
  data_gb: number | null;
  data_unlimited: number;
  validity_days: number | null;
  old_price_usd: number | null;
  new_price_usd: number;
  change_pct: number | null;
  status: 'up' | 'down' | 'no_prior';
  likely_noise?: boolean;
  source_url: string | null;
}

export interface PriceChangesData {
  week: number;
  prior_week: number | null;
  region: string;
  changes: PriceChangeRow[];
  has_prior: boolean;
}

// API functions
export const api = {
  snapshots: {
    latest: () => apiFetch<SnapshotData>(apiUrl('/api/snapshots/latest')),
    byWeek: (week: number) => apiFetch<SnapshotData>(apiUrl(`/api/snapshots/${week}`)),
  },
  providers: {
    list: () => apiFetch<{ data: Provider[]; week: number | null }>(apiUrl('/api/providers'))
      .catch(() => ({ data: [] as Provider[], week: null })),
    detail: (id: string, week?: number) =>
      apiFetch<ProviderDetail>(apiUrl(`/api/providers/${id}`, week ? { week: String(week) } : {})),
  },
  pricing: {
    byWeek: (week: number, region = 'all') =>
      apiFetch<PricingData>(apiUrl(`/api/pricing/${week}`, { region })),
  },
  diffs: {
    byWeek: (week: number) => apiFetch<DiffsData>(apiUrl(`/api/diffs/${week}`)),
  },
  benchmark: {
    byWeek: (week: number, region = 'all') =>
      apiFetch<BenchmarkData>(apiUrl(`/api/benchmark/${week}`, { region })),
  },
  priceChanges: {
    byWeek: (week: number, region = 'all') =>
      apiFetch<PriceChangesData>(apiUrl(`/api/price-changes/${week}`, { region })),
  },
};
